import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import { BadgeWithModal } from "@/components/BadgeWithModal";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { getPublicShareUrl } from "@/lib/publicUrl";

const FallbackAvatar =
  "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=160&h=160&fit=crop";

const getAvatarUrl = (u) => (u ? getProfilePhotoUrl(u) : null);

const ReferralsPage = () => {
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const meRes = await api.get("/auth/me");
        setMe(meRes.data);
        const refRes = await api.get("/referrals/me", {
          params: { current_user_id: meRes.data.user_id },
        });
        setData(refRes.data);
      } catch (e) {
        if (e.response?.status === 401) {
          navigate("/login");
          return;
        }
        toast.error("Could not load referrals");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const referralLink = data?.referral_link
    ? data.referral_link
    : data?.referral_code
      ? getPublicShareUrl(`/?ref=${data.referral_code}`)
      : null;

  const copyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied!");
      // Mark referral shared in onboarding
      try {
        await api.post("/onboarding/share-referral", null, {
          params: { current_user_id: me.user_id },
        });
      } catch (_) { /* non-fatal */ }
    } catch {
      toast.error("Could not copy");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#08080c" }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const total = data?.total || 0;
  const newest = data?.newest || [];
  const all = data?.all || [];

  return (
    <>
      <Helmet>
        <title>Referrals | RGM Studio</title>
      </Helmet>
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={me} activePage="referrals" />

        <div className="max-w-6xl mx-auto pt-24 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight">
              My Referrals
            </h1>
            <p className="text-gray-400 mt-3 max-w-lg">
              Grow the RGM Studio family. Share your link, earn badges, and watch your network build.
            </p>
            <Link to="/affiliate" className="inline-block mt-4 text-sm font-bold text-emerald-300 hover:text-emerald-200 underline" data-testid="referrals-affiliate-link">
              Founding Affiliate Program — earn 20% on referred memberships →
            </Link>
          </div>

          {/* Stats + Link */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 p-6">
              <p className="text-sm text-purple-300 uppercase tracking-widest font-bold">Total Referrals</p>
              <p className="text-5xl font-black text-white mt-2" data-testid="referral-total-count">{total}</p>
              <p className="text-xs text-gray-400 mt-2">Artists who joined through your link</p>
            </Card>

            <Card className="bg-gradient-to-br from-pink-900/30 to-black border border-pink-500/30 p-6">
              <p className="text-sm text-pink-300 uppercase tracking-widest font-bold">Newest Joiner</p>
              {newest[0] ? (
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={optimizeImage(getAvatarUrl(newest[0]) || FallbackAvatar, 'avatar')}
                    alt={newest[0].artist_name || newest[0].name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-pink-500/40"
                    onError={(e) => { e.target.src = FallbackAvatar; }}
                  />
                  <div className="min-w-0">
                    <Link
                      to={`/artist/${newest[0].user_id}`}
                      className="font-bold text-white text-base hover:text-pink-300 truncate block"
                    >
                      {newest[0].artist_name || newest[0].name}
                    </Link>
                    <p className="text-xs text-gray-400">
                      Joined {new Date(newest[0].created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm mt-3">No referrals yet — share your link below!</p>
              )}
            </Card>

            <Card className="bg-gradient-to-br from-yellow-900/30 to-black border border-yellow-500/30 p-6">
              <p className="text-sm text-yellow-300 uppercase tracking-widest font-bold">Badges Earned</p>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="referral-badges">
                {(data?.referral_badges || []).length > 0 ? (
                  (data?.referral_badges || []).map((b) => (
                    <BadgeWithModal key={b} type={b} size="small" />
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">Refer your first artist to start earning</p>
                )}
              </div>
            </Card>
          </div>

          {/* Referral Link */}
          <Card className="bg-black/50 border border-purple-500/30 p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-3">Your Referral Link</h3>
            <div className="flex items-stretch gap-2 flex-col sm:flex-row">
              <input
                readOnly
                value={referralLink || "Link unavailable"}
                className="flex-1 px-4 py-2 bg-gray-900 border border-gray-700 rounded-md text-purple-300 text-sm font-mono"
                data-testid="referral-link-input"
              />
              <Button
                onClick={copyLink}
                disabled={!referralLink}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                data-testid="copy-referral-link"
              >
                Copy Link
              </Button>
            </div>
          </Card>

          {/* All referrals list */}
          <h2 className="text-2xl font-bold text-white mb-4">All Referrals</h2>
          {all.length === 0 ? (
            <Card className="p-8 text-center bg-black/40 border border-purple-500/20" data-testid="referrals-empty">
              <div className="text-5xl mb-3">🤝</div>
              <p className="text-gray-300">No one has joined through your link yet.</p>
              <p className="text-gray-500 text-sm mt-1">Share your link with artist friends to start building your network.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {all.map((u) => (
                <Link
                  key={u.user_id}
                  to={`/artist/${u.user_id}`}
                  className="group flex items-center gap-3 p-4 rounded-xl bg-black/40 border border-purple-500/20 hover:border-purple-500/60 transition-all"
                  data-testid={`referral-${u.user_id}`}
                >
                  <img
                    src={optimizeImage(getAvatarUrl(u) || FallbackAvatar, 'avatar')}
                    alt={u.artist_name || u.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/30 flex-shrink-0"
                    onError={(e) => { e.target.src = FallbackAvatar; }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-white truncate group-hover:text-purple-300 transition-colors">
                      {u.artist_name || u.name}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      Joined {new Date(u.created_at).toLocaleDateString()}
                    </p>
                    {u.badges && u.badges.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {u.badges.slice(0, 2).map((b) => (
                          <BadgeWithModal key={b} type={b} size="small" showLabel={false} />
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReferralsPage;
